/** A throwaway origin to resolve paths against; a path must not leave it. */
const APP_ORIGIN = 'https://app.invalid'

/**
 * The URL parser silently drops tabs and newlines, so `/\t/evil.com` becomes
 * `//evil.com` — another host — after any check made on the raw string.
 */
const hasControlCharacter = (value: string) =>
  [...value].some(char => {
    const code = char.charCodeAt(0)
    return code < 0x20 || code === 0x7f
  })

/**
 * Turns the `action_url` of a notification into a route of this app.
 *
 * The notifications service stores absolute links built from its own config
 * (`https://arena.bcs.com/tasks/share/<id>`), so following them verbatim would
 * send a fan on the stand or on dev to production. The link always points into
 * this front-end, so only its path, query and hash matter.
 *
 * Only an app path or an `https:` link is accepted, and the result is the path
 * as the URL parser normalises it, checked to stay on this site. Anything
 * else — `javascript:`, `//host`, control characters — returns null: a
 * notification never takes the fan off the site.
 */
export function notificationHref(actionUrl?: string): string | null {
  const raw = actionUrl?.trim()
  if (!raw || hasControlCharacter(raw)) {
    return null
  }

  let path = raw
  if (!raw.startsWith('/')) {
    let absolute: URL
    try {
      absolute = new URL(raw)
    } catch {
      return null
    }
    if (absolute.protocol !== 'https:') {
      return null
    }
    // `https://host//evil.com` parses to the path `//evil.com`, which the
    // router would read as another host.
    path = `${absolute.pathname.replace(/^[/\\]+/, '/')}${absolute.search}${
      absolute.hash
    }`
  }

  let url: URL
  try {
    url = new URL(path, APP_ORIGIN)
  } catch {
    return null
  }
  if (url.origin !== APP_ORIGIN) {
    return null
  }

  return `${url.pathname}${url.search}${url.hash}`
}
